export type WizardStatus = 'notStarted' | 'inProgress' | 'complete'

/**
 * Per-section counterpart of `WizardStatus`. Purely a projection of
 * `WizardState` — nothing in the wizard reads it back, so it can never drive
 * navigation. Schema authors bind it to persist where a user got to in a form.
 *
 *  - 'conditional'   queued behind the active section and gated by an
 *                    `entryPredicate`, so it may or may not be reached.
 *  - 'upcoming'      queued behind the active section with no predicate (or a
 *                    predicate that has not been evaluated yet) — it will be
 *                    reached.
 *  - 'active'        currently the visible section.
 *  - 'deferred'      the user tapped Skip; re-enqueued behind everything else.
 *  - 'notApplicable' the wizard reached it and its `entryPredicate` was falsy,
 *                    so it was passed over. Not a latch: a predicate that
 *                    flips truthy moves the section back to 'upcoming'.
 *  - 'complete'      the user advanced past it.
 */
export type UI2FormSectionStatus =
  | 'conditional'
  | 'upcoming'
  | 'active'
  | 'deferred'
  | 'notApplicable'
  | 'complete'

export interface WizardSectionRegistration {
  id: string
  /** Declaration-order index assigned by the form at registration time. */
  index: number
  title?: string
  entryPredicate?: string
  nextMode: 'automatic' | 'submit'
  allowSkip: boolean
}

export interface WizardState {
  /** IDs of sections not yet completed. queue[0] is the currently-active section (or empty when done). */
  queue: string[]
  /** IDs of sections completed, in the order the user completed them. */
  completedStack: string[]
  /** Every section that has ever been the active section. Drives progress-indicator tappability. */
  visitedIds: string[]
  /** Every section that has ever been advanced past. Grows monotonically — jumping back to a completed section pops it from `completedStack` but leaves it in this set. Sections check this to know they are being *re*visited. */
  everCompletedIds: string[]
  /** Every section the user has ever tapped Skip on. Grows monotonically — feeds the 'deferred' section status; completion and predicate evaluation both take precedence over it. */
  deferredIds: string[]
  /** Currently active section id, or null before the first section resolves. */
  activeId: string | null
}

export function initialWizardState(): WizardState {
  return {
    queue: [],
    completedStack: [],
    visitedIds: [],
    everCompletedIds: [],
    deferredIds: [],
    activeId: null,
  }
}

/**
 * Advance past `currentId`, then dequeue any following sections whose
 * predicate evaluates falsy. `evalPredicate` is invoked with the section's
 * declared expression string; a section with no predicate is always truthy.
 */
export function advance(
  state: WizardState,
  currentId: string,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const queue = state.queue.filter((id) => id !== currentId)
  const completedStack = [...state.completedStack, currentId]
  const activeId = pickNext(queue, registrations, evalPredicate)
  return {
    queue,
    completedStack,
    visitedIds: addVisited(state.visitedIds, activeId),
    everCompletedIds: addVisited(state.everCompletedIds, currentId),
    deferredIds: state.deferredIds,
    activeId,
  }
}

/**
 * Send the current section to the back of the queue so it comes around
 * again after every other section resolves.
 */
export function skip(
  state: WizardState,
  currentId: string,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const rest = state.queue.filter((id) => id !== currentId)
  const queue = [...rest, currentId]
  const activeId = pickNext(queue, registrations, evalPredicate)
  return {
    queue,
    completedStack: state.completedStack,
    visitedIds: addVisited(state.visitedIds, activeId),
    everCompletedIds: state.everCompletedIds,
    deferredIds: addVisited(state.deferredIds, currentId),
    activeId,
  }
}

/**
 * Pop the last completed section and re-enter it.
 */
export function back(state: WizardState): WizardState {
  if (state.completedStack.length === 0) return state
  const completedStack = state.completedStack.slice(0, -1)
  const previous = state.completedStack[state.completedStack.length - 1]
  const queue = [previous, ...state.queue.filter((id) => id !== previous)]
  return {
    queue,
    completedStack,
    visitedIds: addVisited(state.visitedIds, previous),
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: previous,
  }
}

/**
 * Jump directly to a previously visited section from the progress indicator.
 *
 * If the target was completed, every section on top of it in the completed
 * stack (and the target itself) is popped so a subsequent Back navigates to
 * the logically-correct previous step. All uncompleted sections are then
 * re-ordered in declaration order behind the target, discarding any prior
 * skip ordering — after a jump the wizard resumes in its natural order.
 */
export function jumpTo(
  state: WizardState,
  targetId: string,
  registrations: Map<string, WizardSectionRegistration>,
): WizardState {
  if (state.activeId === targetId) return state
  const completedIdx = state.completedStack.indexOf(targetId)
  // Only completed sections are valid jump targets. A skipped-but-not-yet-completed
  // section is only reachable through the normal wizard flow.
  if (completedIdx < 0) return state
  const newCompletedStack = state.completedStack.slice(0, completedIdx)

  const completedSet = new Set(newCompletedStack)
  const pending = Array.from(registrations.values())
    .sort((a, b) => a.index - b.index)
    .map((r) => r.id)
    .filter((id) => !completedSet.has(id) && id !== targetId)

  return {
    queue: [targetId, ...pending],
    completedStack: newCompletedStack,
    visitedIds: state.visitedIds,
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: targetId,
  }
}

/**
 * Make `targetId` the active section, either direction.
 *
 * Backward (target on `completedStack`): identical to `jumpTo` — the stack is
 * rewound so a subsequent Back navigates to the logically-correct previous
 * step, and pending sections are re-ordered in declaration order.
 *
 * Forward (target still in the queue, or previously skipped): the current
 * queue is reordered so `targetId` sits at the front. `completedStack` is
 * NOT touched — the user hasn't actually completed any of the sections
 * we're jumping past — so those pending sections remain in the queue and
 * come back around after the target resolves.
 *
 * Used by save-flow "focus first error" navigation, which must be able to
 * reveal an errored field regardless of whether the user has been there
 * yet. Progress-indicator tap navigation should keep using `jumpTo` so the
 * completed-only constraint (tap only what you've already finished) is
 * preserved.
 */
export function revealSection(
  state: WizardState,
  targetId: string,
  registrations: Map<string, WizardSectionRegistration>,
): WizardState {
  if (state.activeId === targetId) return state
  if (state.completedStack.includes(targetId)) {
    return jumpTo(state, targetId, registrations)
  }
  if (!registrations.has(targetId)) return state
  const pending = Array.from(registrations.values())
    .sort((a, b) => a.index - b.index)
    .map((r) => r.id)
    .filter((id) => !state.completedStack.includes(id) && id !== targetId)
  return {
    queue: [targetId, ...pending],
    completedStack: state.completedStack,
    visitedIds: addVisited(state.visitedIds, targetId),
    everCompletedIds: state.everCompletedIds,
    deferredIds: state.deferredIds,
    activeId: targetId,
  }
}

/**
 * Would `advance` from the current active section empty the queue? Used to
 * label the active section's Next button as "Complete" and to preview the
 * success state.
 */
export function willEmptyQueueOnAdvance(
  state: WizardState,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): boolean {
  if (!state.activeId) return false
  const rest = state.queue.filter((id) => id !== state.activeId)
  return pickNext(rest, registrations, evalPredicate) === null
}

export function derivedStatus(state: WizardState): WizardStatus {
  if (state.queue.length === 0 && state.completedStack.length > 0) return 'complete'
  if (state.completedStack.length === 0) return 'notStarted'
  return 'inProgress'
}

/**
 * Project the wizard state onto a per-section status, keyed by section id.
 *
 * Read-only: nothing in the controller consumes the result, so a status can
 * never feed back into navigation. Recompute on every render alongside
 * `progressSteps` — `entryPredicate` reads live field values, so a memoized
 * result would go stale.
 *
 * `notApplicable` is decided positionally. `pickNext` scans the queue front to
 * back and stops at the first truthy predicate, so every queued section
 * *ahead* of the active one was evaluated and rejected; sections behind it
 * have not been evaluated yet. When the queue has resolved (`activeId` is
 * null) everything still queued has been evaluated.
 */
export function deriveSectionStatuses(
  state: WizardState,
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): Record<string, UI2FormSectionStatus> {
  const completed = new Set(state.completedStack)
  const deferred = new Set(state.deferredIds)
  // Sections before this index in the queue have had their predicate evaluated.
  // A null (or unqueued) active section means the scan ran off the end, so
  // everything left in the queue has been evaluated.
  const rawActiveIdx = state.activeId == null ? -1 : state.queue.indexOf(state.activeId)
  const activeIdx = rawActiveIdx < 0 ? state.queue.length : rawActiveIdx
  const statuses: Record<string, UI2FormSectionStatus> = {}
  registrations.forEach((reg, id) => {
    if (completed.has(id)) {
      statuses[id] = 'complete'
      return
    }
    if (state.activeId === id) {
      statuses[id] = 'active'
      return
    }
    const queueIdx = state.queue.indexOf(id)
    const wasEvaluated = queueIdx >= 0 && queueIdx < activeIdx
    if (wasEvaluated && reg.entryPredicate != null && !evalPredicate(reg.entryPredicate)) {
      statuses[id] = 'notApplicable'
      return
    }
    if (deferred.has(id)) {
      statuses[id] = 'deferred'
      return
    }
    statuses[id] = reg.entryPredicate != null ? 'conditional' : 'upcoming'
  })
  return statuses
}

/** Choose the next section from the queue by evaluating predicates in order. */
function pickNext(
  queue: string[],
  registrations: Map<string, WizardSectionRegistration>,
  evalPredicate: (expr: string) => boolean,
): string | null {
  for (const id of queue) {
    const reg = registrations.get(id)
    if (!reg) continue
    if (reg.entryPredicate == null || evalPredicate(reg.entryPredicate)) return id
  }
  return null
}

/** Drop leading queue entries whose predicate returned false. */
function dropUntil(queue: string[], activeId: string | null): string[] {
  if (activeId == null) return []
  const idx = queue.indexOf(activeId)
  return idx < 0 ? queue : queue.slice(idx)
}

function addVisited(visited: string[], id: string | null): string[] {
  if (id == null || visited.includes(id)) return visited
  return [...visited, id]
}

/**
 * Build the initial queue for a freshly-registered set of sections. Called
 * once when the form has finished collecting its initial registrations.
 */
export function seedQueue(
  registrations: WizardSectionRegistration[],
  evalPredicate: (expr: string) => boolean,
): WizardState {
  const sorted = [...registrations].sort((a, b) => a.index - b.index)
  const ids = sorted.map((r) => r.id)
  const registrationMap = new Map(sorted.map((r) => [r.id, r]))
  const activeId = pickNext(ids, registrationMap, evalPredicate)
  return {
    queue: ids,
    completedStack: [],
    visitedIds: activeId ? [activeId] : [],
    everCompletedIds: [],
    deferredIds: [],
    activeId,
  }
}
